import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getBooleanField, getMinCountError } from '@lender/utils';
import { Button, FormHelperText, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getSharedFormStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS, CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import clsx from 'clsx';
import _ from 'lodash';
import { PropTypes } from 'prop-types';
import { array, object } from 'yup';

import {
  createDefaultCreditBureaus,
  CREDIT_BUREAU_MIN_ERROR,
  creditBureauFields,
  isAnyCreditBureauSelected,
} from 'constants/loanActionEditor';
import useForm from 'hooks/useForm';

import { closeDialogAction } from 'store/global/actions';

import { LoanHeadersTestIds } from '../../../LoanHeaders.testIds';

import { ConfirmationPreSubmitModalTestIds } from './ConfirmationPreSubmitModal.testIds';

const getSchema = ({ withOptionsSelection, withBureauSelection }) =>
  object().shape({
    isWrittenPermission: getBooleanField(),
    isVerbalPermission: getBooleanField(),
    ...(withOptionsSelection && {
      options: array().of(object()).min(1, getMinCountError('option')),
    }),
    ...(withBureauSelection && {
      bureaus: object().test('at-least-one-bureau', CREDIT_BUREAU_MIN_ERROR, value =>
        isAnyCreditBureauSelected(value),
      ),
    }),
  });

const ModalButtonType = {
  ACCEPT: 'accept',
  DECLINE: 'decline',
};

const checkBoxTypeAttributes = {
  WRITTEN_TYPE: { 'data-permission-type': 'isWrittenPermission' },
  VERBAL_TYPE: { 'data-permission-type': 'isVerbalPermission' },
};

const defaultOptions = [];

const ConfirmationPreSubmitModal = ({
  isActiveDeclineButtonStyle = false,
  onAccept = _.noop,
  onDecline = _.noop,
  message = '',
  buttonAcceptText = 'Yes',
  buttonDeclineText = 'No',
  withPermissionTypeCheck = false,
  withOptionsSelection = false,
  withBureauSelection = false,
  initialBureaus = null,
  options = defaultOptions,
  selectLabel = '',
  noChoiceText = '',
}) => {
  const isMountedRef = useRef(true);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useMemo(() => getSharedFormStyles(theme), [theme]);
  const declineButtonStyle = isActiveDeclineButtonStyle
    ? classes.submitButton
    : classes.cancelButton;

  const isOptionsEmpty = !options.length;
  const isNoChoiceState = withOptionsSelection && isOptionsEmpty;

  const {
    form,
    handleChange,
    validateField,
    setInForm,
    isFormChanged,
    validateForm,
    setSchema,
    validationErrors,
  } = useForm({
    isWrittenPermission: false,
    isVerbalPermission: false,
    options: [],
    bureaus: initialBureaus ?? createDefaultCreditBureaus(),
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const schema = getSchema({ withOptionsSelection, withBureauSelection });

    setSchema(schema);
  }, [setSchema, withOptionsSelection, withBureauSelection]);

  const isPermissionGranted = useMemo(
    () => (form.isWrittenPermission || form.isVerbalPermission) && !isSubmitted,
    [form, isSubmitted],
  );

  const isBureauSelectionValid = !withBureauSelection || isAnyCreditBureauSelected(form.bureaus);

  const onDeclineClick = useCallback(async () => {
    setIsSubmitted(true);

    try {
      await onDecline(ModalButtonType.DECLINE);
    } finally {
      dispatch(closeDialogAction());
    }
  }, [dispatch, onDecline]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      const shouldValidate = withPermissionTypeCheck || withBureauSelection || withOptionsSelection;
      // A bureau-only dialog can be submitted with the config defaults untouched, so the
      // unchanged-form shortcut inside validateForm must not skip the schema.
      const validationContext = withBureauSelection ? { noFormChangedValidate: true } : undefined;

      if (shouldValidate && !(await validateForm(validationContext))) {
        return;
      }

      setIsSubmitted(true);

      try {
        await onAccept(ModalButtonType.ACCEPT, form);
      } finally {
        if (isMountedRef.current) {
          setIsSubmitted(false);
        }
      }
    },
    [
      form,
      onAccept,
      validateForm,
      withPermissionTypeCheck,
      withBureauSelection,
      withOptionsSelection,
    ],
  );

  return (
    <form
      noValidate
      className="flex flex-col items-stretch"
      data-ischanged={isFormChanged}
      data-testid={LoanHeadersTestIds.WAREHOUSE_FORM}
      data-type={ModalButtonType.ACCEPT}
      onSubmit={onSubmit}
    >
      <div className="flex flex-col justify-center">
        <div className="flex flex-col">
          {message && (
            <Typography className="mb-20" variant="bold16">
              {message}
            </Typography>
          )}

          {withPermissionTypeCheck && (
            <div className="flex items-start">
              <CheckboxCDS
                dataTestId={ConfirmationPreSubmitModalTestIds.WRITTEN_CHECKBOX}
                disabled={isSubmitted}
                inputProps={checkBoxTypeAttributes.WRITTEN_TYPE}
                label="Written"
                name="isWrittenPermission"
                value={form.isWrittenPermission}
                onChange={handleChange}
              />

              <CheckboxCDS
                dataTestId={ConfirmationPreSubmitModalTestIds.VERBAL_CHECKBOX}
                disabled={isSubmitted}
                inputProps={checkBoxTypeAttributes.VERBAL_TYPE}
                label="Verbal"
                name="isVerbalPermission"
                value={form.isVerbalPermission}
                variant="bold16"
                onChange={handleChange}
              />
            </div>
          )}

          {withBureauSelection && (
            <div className="mb-16 flex flex-col">
              <Typography variant="bold16">Credit bureaus</Typography>
              <div className="flex items-start">
                {creditBureauFields.map(({ name, key }) => (
                  <CheckboxCDS
                    noValidate
                    dataTestId={ConfirmationPreSubmitModalTestIds.getBureauCheckboxId(key)}
                    disabled={isSubmitted}
                    error={!isBureauSelectionValid}
                    key={key}
                    label={name}
                    name={`bureaus.${key}`}
                    value={!!form.bureaus?.[key]}
                    onChange={handleChange}
                  />
                ))}
              </div>
              {!isBureauSelectionValid && (
                <FormHelperText error>{CREDIT_BUREAU_MIN_ERROR}</FormHelperText>
              )}
            </div>
          )}

          {withOptionsSelection && !isOptionsEmpty && (
            <AutocompleteCDS
              multiple
              required
              containerClassName="mb-24"
              dataTestId={ConfirmationPreSubmitModalTestIds.OPTIONS_SELECT}
              error={!!validationErrors.options}
              helperText={validationErrors.options}
              label={selectLabel}
              name="options"
              optionName="name"
              options={options}
              value={form.options}
              onChange={onAutocompleteChange}
            />
          )}

          {isNoChoiceState && (
            <Typography className="mb-20" variant="medium16">
              {noChoiceText}
            </Typography>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            className={clsx(declineButtonStyle, 'min-w-80')}
            data-testid={ConfirmationPreSubmitModalTestIds.DECLINE_BUTTON}
            data-type={ModalButtonType.DECLINE}
            disabled={isSubmitted}
            type="button"
            variant="contained"
            onClick={onDeclineClick}
          >
            {buttonDeclineText}
          </Button>

          {!isNoChoiceState && (
            <Button
              className="min-w-80 ml-16"
              color="primary"
              data-testid={ConfirmationPreSubmitModalTestIds.ACCEPT_BUTTON}
              disabled={
                (withPermissionTypeCheck ? !isPermissionGranted : isSubmitted) ||
                !isBureauSelectionValid
              }
              sx={classes.submitButton}
              type="submit"
              variant="contained"
            >
              {buttonAcceptText}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};

ConfirmationPreSubmitModal.propTypes = {
  isActiveDeclineButtonStyle: PropTypes.bool,
  withPermissionTypeCheck: PropTypes.bool,
  options: PropTypes.array,
  withOptionsSelection: PropTypes.bool,
  withBureauSelection: PropTypes.bool,
  initialBureaus: PropTypes.object,
  message: PropTypes.string,
  buttonAcceptText: PropTypes.string,
  buttonDeclineText: PropTypes.string,
  noChoiceText: PropTypes.string,
  selectLabel: PropTypes.string,
  onAccept: PropTypes.func,
  onDecline: PropTypes.func,
};

export default ConfirmationPreSubmitModal;
